/**
 * vat-toggle.js
 * =============
 * Toggles Shopify prices between ex-VAT and inc-VAT (20% UK).
 *
 * Assumptions:
 *   - PRICES_INCLUDE_VAT says how prices are stored in Shopify (true since the
 *     switch to VAT-inclusive pricing; false = stored EXCLUSIVE of VAT).
 *   - Toggle UI is rendered via snippets/vat-toggle.liquid.
 *
 * How to customise:
 *   - Change VAT_RATE below for a different rate (e.g. 0.23 for 23%).
 *   - Add extra selectors to PRICE_SELECTORS if your theme uses custom elements.
 *   - Default state is inc-VAT (VAT_DEFAULT = true). Set to false to default to ex-VAT.
 */

(function () {
  'use strict';

  /* ── Configuration ─────────────────────────────────────────── */
  var VAT_RATE           = 0.20;          // 20% UK VAT
  var PRICES_INCLUDE_VAT = true;          // Shopify prices are stored inc-VAT
  var STORAGE_KEY        = 'vatToggleV2'; // new key: everyone starts on the new default
  var VAT_DEFAULT        = true;          // true = inc-VAT on first visit

  /**
   * CSS selectors that match price elements across:
   *   - Shopify Dawn theme (v6+)
   *   - Common legacy themes
   *   - Cart totals
   *
   * The script stores the ORIGINAL (as-stored) price as a data attribute
   * the first time it encounters each element, so it never double-applies VAT.
   */
  var PRICE_SELECTORS = [
    /* Dawn / Online Store 2.0 */
    '.price__item--regular',
    '.price__item--sale',
    '.price__item--last',
    '.price-item--regular',
    '.price-item--sale',
    '.price-item--last',

    /* Generic product price elements */
    '[data-product-price]',
    '.product__price .price',
    '.product-single__price',
    '.product-price',

    /* Collection grid cards */
    '.card__price .price__item',
    '.card-information .price__item',
    '.product-card .price',

    /* Cart line items */
    '.cart-item__price',
    '.cart-item__discounted-prices .price',
    'td.cart__price',

    /* Cart totals / order summary */
    '.totals__subtotal-value',
    '.totals__total-value',
    '[data-cart-subtotal]',
    '.cart__subtotal .price',
    '.order-summary__emphasis',
    '.cart__total-price',

    /* Fallback — plain .price elements NOT already targeted above */
    '.price:not(.price__item--regular):not(.price__item--sale)',
  ].join(', ');

  /* ── Helpers ───────────────────────────────────────────────── */

  /**
   * Extract numeric value from a formatted money string.
   * Handles: "£10.00", "£1,299.99", "$10.00", "10.00", "€9,99" (comma decimal)
   */
  function parseMoney(str) {
    // Detect comma-as-decimal: "9,99" (no thousands separator pattern)
    var commaDecimal = /^\D*\d{1,3},\d{2}\D*$/.test(str.trim());
    var cleaned = str.replace(/[^\d.,]/g, '');
    if (commaDecimal) {
      cleaned = cleaned.replace(',', '.');
    } else {
      cleaned = cleaned.replace(/,/g, ''); // strip thousands separators
    }
    return parseFloat(cleaned) || 0;
  }

  /** Pull the currency symbol + any prefix from a money string */
  function getCurrencySymbol(str) {
    var match = str.trim().match(/^[^\d\s]+/);
    return match ? match[0] : '£';
  }

  /** Get any suffix after the number (e.g. " GBP") */
  function getCurrencySuffix(str) {
    var match = str.trim().match(/[^\d.,]+$/);
    return (match && match[0].trim()) ? match[0] : '';
  }

  /** Format a number back to a money string */
  function formatMoney(amount, symbol, suffix) {
    var formatted = amount.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return (symbol || '£') + formatted + (suffix ? ' ' + suffix.trim() : '');
  }

  /* ── Core logic ────────────────────────────────────────────── */

  /**
   * Walk all price elements and stamp data-ex-vat-price on first encounter.
   * Safe to call multiple times — never overwrites an existing stamp.
   */
  function stampPrices() {
    var els = document.querySelectorAll(PRICE_SELECTORS);
    for (var i = 0; i < els.length; i++) {
      var el = els[i];
      if (el.dataset.exVatPrice) continue; // already stamped

      var text = el.textContent.trim();
      if (!text || text === '-' || text === '') continue;

      var amount = parseMoney(text);
      if (!amount || isNaN(amount)) continue;

      el.dataset.exVatPrice      = amount.toString();
      el.dataset.vatSymbol       = getCurrencySymbol(text);
      el.dataset.vatSuffix       = getCurrencySuffix(text);
    }
  }

  /**
   * Re-render every stamped element for the given VAT state.
   */
  function applyPrices(showVat) {
    var els = document.querySelectorAll('[data-ex-vat-price]');
    for (var i = 0; i < els.length; i++) {
      var el     = els[i];
      // data-ex-vat-price holds the price as stored (name kept for compatibility).
      var base   = parseFloat(el.dataset.exVatPrice);
      var symbol = el.dataset.vatSymbol || '£';
      var suffix = el.dataset.vatSuffix || '';
      var amount;
      if (PRICES_INCLUDE_VAT) {
        amount = showVat ? base : base / (1 + VAT_RATE);
      } else {
        amount = showVat ? base * (1 + VAT_RATE) : base;
      }
      el.textContent = formatMoney(amount, symbol, suffix);
    }

    // Update the toggle wrapper's data attribute (drives CSS label highlighting)
    var wrapper = document.getElementById('vat-toggle');
    if (wrapper) {
      wrapper.dataset.vat = showVat ? 'on' : 'off';
    }
  }

  /** Full update cycle: stamp then render */
  function refresh(showVat) {
    stampPrices();
    applyPrices(showVat);
  }

  /* ── State management ──────────────────────────────────────── */

  function getStoredState() {
    var val = localStorage.getItem(STORAGE_KEY);
    if (val === null) return VAT_DEFAULT;
    return val === '1';
  }

  function saveState(showVat) {
    localStorage.setItem(STORAGE_KEY, showVat ? '1' : '0');
  }

  /* ── Initialisation ────────────────────────────────────────── */

  function init() {
    var showVat  = getStoredState();
    var checkbox = document.getElementById('vat-toggle-input');

    if (checkbox) {
      checkbox.checked = showVat;

      checkbox.addEventListener('change', function () {
        var newState = this.checked;
        saveState(newState);
        // NOTE: do NOT clear existing stamps here.
        // data-ex-vat-price is the permanent ground-truth base price.
        // Clearing it and re-stamping would re-read whatever is currently
        // on screen (possibly already inc-VAT), causing prices to compound
        // on every toggle. Just re-apply from the stored base.
        applyPrices(newState);
      });
    }

    // Initial render
    refresh(showVat);
  }

  /* ── Handle dynamic content (AJAX cart, section reloads) ───── */

  /**
   * MutationObserver watches for new price elements injected by
   * Shopify's section rendering API or AJAX cart updates.
   * Debounced to avoid hammering on rapid mutations.
   */
  var debounceTimer = null;

  function onMutation() {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(function () {
      var showVat = getStoredState();
      // Only re-apply when the display differs from how prices are stored.
      if (showVat !== PRICES_INCLUDE_VAT) refresh(showVat);
    }, 150);
  }

  function startObserver() {
    if (!window.MutationObserver) return;
    var observer = new MutationObserver(onMutation);
    observer.observe(document.body, { childList: true, subtree: true });
  }

  // Shopify section:load fires when a section is re-rendered
  document.addEventListener('shopify:section:load', function () {
    setTimeout(function () {
      var showVat = getStoredState();
      refresh(showVat);
      // Re-wire the checkbox if it was inside the reloaded section
      var checkbox = document.getElementById('vat-toggle-input');
      if (checkbox && !checkbox._vatBound) {
        checkbox._vatBound = true;
        checkbox.checked = showVat;
        checkbox.addEventListener('change', function () {
          saveState(this.checked);
          applyPrices(this.checked); // use applyPrices — never re-stamp on toggle
        });
      }
    }, 50);
  });

  /* ── Boot ──────────────────────────────────────────────────── */

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
      startObserver();
    });
  } else {
    init();
    startObserver();
  }

})();